import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { FileText, Image, Code, Archive, File, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImageZoom } from '../ui/kibo-ui/image-zoom';

export interface FileAttachment {
  id: string;
  url: string;
  thumbnailUrl?: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  analysis?: {
    summary: string;
    contentType: 'image' | 'document' | 'code' | 'text' | 'other';
    extractedText?: string;
    keyPoints?: string[];
    suggestions?: string[];
  };
}

interface FileAttachmentProps {
  file: FileAttachment;
  onRemove?: (fileId: string) => void;
  showRemoveButton?: boolean;
  compact?: boolean;
  className?: string;
}

const FileAttachmentComponent: React.FC<FileAttachmentProps> = ({
  file,
  onRemove,
  showRemoveButton = false,
  compact = false,
  className,
}) => {
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (mimeType.startsWith('text/') || mimeType.includes('javascript') || mimeType.includes('typescript')) {
      return <Code className="w-4 h-4" />;
    }
    if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word')) {
      return <FileText className="w-4 h-4" />;
    }
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive')) {
      return <Archive className="w-4 h-4" />;
    }
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getContentTypeColor = (contentType: string) => {
    switch (contentType) {
      case 'image': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'document': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'code': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'text': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      default: return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    }
  };

  const handleCardClick = () => {
    if (file.mimeType.startsWith('image/')) {
      // For images, the ImageZoom handles the interaction
      return;
    } else {
      window.open(file.url, '_blank');
    }
  };

  if (compact) {
    const handleClick = () => {
      if (file.mimeType.startsWith('image/')) {
        // For images, the ImageZoom handles the interaction
        return;
      } else {
        window.open(file.url, '_blank');
      }
    };

    return (
      <div 
        className={cn(
          'flex items-center space-x-3 p-3 rounded-lg border transition-colors',
          'bg-card text-card-foreground',
          'border-border hover:bg-accent/50',
          !file.mimeType.startsWith('image/') && 'cursor-pointer',
          className
        )}
        onClick={!file.mimeType.startsWith('image/') ? handleClick : undefined}
      >
        {file.thumbnailUrl ? (
          <ImageZoom>
            <img
              src={file.url}
              alt={file.fileName}
              className="w-10 h-10 object-cover rounded-md border border-border cursor-pointer"
            />
          </ImageZoom>
        ) : (
          <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center border border-border">
            {getFileIcon(file.mimeType)}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{file.fileName}</p>
          <p className="text-xs text-muted-foreground">{formatFileSize(file.fileSize)}</p>
        </div>
        
        <div className="flex items-center space-x-1">
          {showRemoveButton && onRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(file.id);
              }}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card 
      className={cn(
        'transition-colors hover:bg-accent/30',
        !file.mimeType.startsWith('image/') && 'cursor-pointer',
        className
      )}
      onClick={!file.mimeType.startsWith('image/') ? handleCardClick : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            {file.thumbnailUrl ? (
              <ImageZoom>
                <img
                  src={file.url}
                  alt={file.fileName}
                  className="w-16 h-16 object-cover rounded-lg border border-border cursor-pointer"
                />
              </ImageZoom>
            ) : (
              <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center border border-border">
                {getFileIcon(file.mimeType)}
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{file.fileName}</p>
              <p className="text-xs text-muted-foreground mb-2">
                {formatFileSize(file.fileSize)} • {file.mimeType}
              </p>
              
              {file.analysis && (
                <div className="mt-2 space-y-2">
                  <Badge 
                    variant="secondary" 
                    className={cn(
                      "text-xs",
                      getContentTypeColor(file.analysis.contentType)
                    )}
                  >
                    {file.analysis.contentType}
                  </Badge>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {file.analysis.summary}
                  </p>
                  {file.analysis.keyPoints && file.analysis.keyPoints.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {file.analysis.keyPoints.slice(0, 3).map((point, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {point.length > 20 ? point.substring(0, 20) + '...' : point}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-1 ml-2">
            {showRemoveButton && onRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(file.id);
                }}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FileAttachmentComponent;